// Vital-Portal — frontend entry.
//
// Three jobs:
//   1. Pull in the SCSS so Vite emits a hashed CSS file alongside the JS
//      bundle (Jinja templates render <link href="{{ vite_asset('main.ts').css }}"> ).
//   2. The cookie-banner show/hide IIFE — the only client-side state we
//      ship right now. Reads/writes a single localStorage flag so the
//      banner stops appearing after the user dismisses it.
//   3. Define the HTMX base URL so any inline `hx-*` attributes the
//      templates emit go to the same origin (Flask). HTMX itself is
//      loaded via a <script> tag in the base template — we just set
//      the global config object before it executes.

import "./styles/main.scss";
// V2-002 — instant catalog filter. Module self-attaches; safe on pages without
// the #catalog-grid-region selector (it just sits idle there).
import "./scripts/instant_filter";
// V2-004 — cart slide-out drawer + add-to-cart toast.
import "./scripts/cart_animations";
// V2-round-3 — landing-page motion: stat count-up + auto-scrolling carousels.
import "./scripts/v2_motion";
// Deferred-2 — search autocomplete on every <input type="search" name="q">.
import "./scripts/search_autocomplete";

// Track 2.8.b — bundle Bootstrap JS locally so carousel arrows, offcanvas
// menus and other Bootstrap components work under the strict CSP
// (script-src 'self' … blocks cdn.jsdelivr.net). The dropdown hover-open
// init lives in _base.html as pure-DOM JS (no bootstrap.* dependency) so
// the Produse menu opens even before this module finishes loading.
import * as bootstrap from "bootstrap";
declare global { interface Window { bootstrap: typeof bootstrap; } }
window.bootstrap = bootstrap;

declare global {
  interface Window {
    htmx?: { config?: Record<string, unknown> };
  }
}

// ── Cookie banner ────────────────────────────────────────────────────────
//
// The Romanian GDPR-style banner is rendered server-side by the public
// blueprint. We only flip its visibility based on localStorage so the
// banner can hydrate before this script runs (no FOUC).
(() => {
  const KEY = "vl-cookies-acknowledged";
  const banner = document.querySelector<HTMLElement>("[data-cookie-banner]");
  if (!banner) return;

  const acknowledged = (() => {
    try {
      return localStorage.getItem(KEY) === "1";
    } catch {
      // Safari private mode + iframe sandboxes throw on localStorage;
      // treat as "not acknowledged yet" rather than crashing the page.
      return false;
    }
  })();

  if (acknowledged) {
    banner.hidden = true;
    return;
  }

  banner.hidden = false;
  banner.querySelector<HTMLButtonElement>("[data-cookie-accept]")?.addEventListener(
    "click",
    () => {
      try {
        localStorage.setItem(KEY, "1");
      } catch {
        /* swallow — see above */
      }
      banner.hidden = true;
    },
    { once: true },
  );
})();

// ── HTMX base config ─────────────────────────────────────────────────────
//
// Tell HTMX (when it loads) to send a CSRF header on every non-GET
// request. The token is rendered as a meta tag in the base template by
// Flask-WTF. We don't import HTMX itself here — the base.html <script>
// tag does — we only set the config object up so HTMX picks it up on
// boot.
window.htmx = window.htmx ?? {};
window.htmx.config = {
  ...(window.htmx.config ?? {}),
  // Honour the meta name="csrf-token" rendered by Flask-WTF.
  includeIndicatorStyles: false,
  defaultSwapStyle: "innerHTML",
};

// ── V2 Core Web Vitals collector ─────────────────────────────────────────
//
// Pure-DOM implementation of the LCP / CLS / INP / FCP measurements (no
// npm dep — keeps the bundle small and avoids supply-chain risk). Sends
// each sample via sendBeacon to /v2/cwv. Tolerant of older browsers — every
// PerformanceObserver call is wrapped so an unsupported entry type doesn't
// crash the page.
//
// Schema on the wire: { metric: "LCP", value: 1234, page: "/produse/", connection: "4g" }
// Backend stores in v2.cwv_observations (Postgres-only).
(() => {
  const ENDPOINT = "/v2/cwv";
  const conn = (navigator as any).connection?.effectiveType || "";

  function send(metric: string, value: number) {
    if (!Number.isFinite(value) || value < 0) return;
    const body = JSON.stringify({
      metric,
      value: Math.round(value * 100) / 100,
      page: location.pathname,
      connection: conn,
    });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      } else {
        fetch(ENDPOINT, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true });
      }
    } catch { /* swallow — beacon is best-effort */ }
  }

  // LCP — last entry wins; report on hidden/pagehide.
  let lcpValue = 0;
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries() as PerformanceEntry[];
      const last = entries[entries.length - 1] as any;
      if (last && last.startTime > lcpValue) lcpValue = last.startTime;
    }).observe({ type: "largest-contentful-paint", buffered: true });
  } catch { /* unsupported */ }

  // CLS — sum of layout-shift values that are NOT user-input-triggered.
  let clsValue = 0;
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) clsValue += entry.value;
      }
    }).observe({ type: "layout-shift", buffered: true });
  } catch { /* unsupported */ }

  // INP — track the worst interaction's processing time (rough proxy; full
  // INP needs more careful event-timing tracking, but worst processingStart
  // → processingEnd is a reasonable approximation for field data).
  let inpValue = 0;
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        const dur = entry.duration ?? 0;
        if (dur > inpValue) inpValue = dur;
      }
    }).observe({ type: "event", buffered: true, durationThreshold: 16 } as any);
  } catch { /* unsupported */ }

  // FCP from PaintTiming.
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") send("FCP", entry.startTime);
      }
    }).observe({ type: "paint", buffered: true });
  } catch { /* unsupported */ }

  // TTFB from Navigation Timing.
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav) send("TTFB", nav.responseStart);
  } catch { /* unsupported */ }

  // Flush LCP + CLS + INP on visibility change / pagehide (more reliable than unload).
  function flush() {
    if (lcpValue > 0) send("LCP", lcpValue);
    send("CLS", clsValue);
    if (inpValue > 0) send("INP", inpValue);
  }
  addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); }, { once: true });
  addEventListener("pagehide", flush, { once: true });
})();
